import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Repeat, Mail, Eye, CheckCircle2, XCircle, Clock, X, ExternalLink } from 'lucide-react';
import { getAdminSubscriptions, cancelAdminSubscription, getAdminSubscriptionInvoices } from '@/lib/api';
import { toast } from 'sonner';

const statusConfig = {
  active: { label: 'Attivo', color: '#10B981', bg: 'rgba(16,185,129,0.1)', icon: CheckCircle2 },
  pending: { label: 'In attesa', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', icon: Clock },
  canceled: { label: 'Annullato', color: '#EF4444', bg: 'rgba(239,68,68,0.1)', icon: XCircle },
  past_due: { label: 'Scaduto', color: 'var(--text-muted)', bg: 'rgba(148,163,184,0.1)', icon: Clock },
  incomplete: { label: 'Incompleto', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', icon: Clock },
  unpaid: { label: 'Non pagato', color: '#EF4444', bg: 'rgba(239,68,68,0.1)', icon: XCircle },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.pending;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs" style={{ backgroundColor: cfg.bg, color: cfg.color, fontFamily: 'var(--font-mono)' }}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

export default function AdminSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAdminSubscriptions();
      setSubs(res.data);
    } catch { toast.error('Errore caricamento abbonamenti'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openDetail = async (s) => {
    setSelected(s); setDetailOpen(true); setInvoices([]);
    try {
      const res = await getAdminSubscriptionInvoices(s.app_subscription_id);
      setInvoices(res.data);
    } catch {
      // no invoices yet (subscription not activated)
    }
  };

  const handleCancel = async () => {
    if (!selected) return;
    if (!window.confirm(`Sei sicuro di voler annullare questo abbonamento?\n\nCliente: ${(selected.billing || {}).name}\nQuesta azione e' irreversibile.`)) return;
    setCancelBusy(true);
    try {
      await cancelAdminSubscription(selected.app_subscription_id);
      toast.success('Abbonamento annullato');
      setDetailOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante annullamento');
    } finally {
      setCancelBusy(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
    </div>
  );

  const active = subs.filter(s => s.status === 'active').length;
  const pending = subs.filter(s => s.status === 'pending').length;
  const canceled = subs.filter(s => s.status === 'canceled').length;

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div>
        <h1 className="text-2xl font-medium tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)' }}>Abbonamenti Rivista</h1>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Quintessenza Internazionale - Sottoscrizioni ricorrenti</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-sm" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                <Repeat className="w-4 h-4" style={{ color: '#10B981' }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Attivi</p>
                <p className="text-2xl font-medium" style={{ fontFamily: 'var(--font-heading)' }} data-testid="sub-count-active">{active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-sm" style={{ backgroundColor: 'rgba(245,158,11,0.1)' }}>
                <Clock className="w-4 h-4" style={{ color: '#F59E0B' }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>In attesa</p>
                <p className="text-2xl font-medium" style={{ fontFamily: 'var(--font-heading)' }} data-testid="sub-count-pending">{pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-sm" style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
                <XCircle className="w-4 h-4" style={{ color: '#EF4444' }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Annullati</p>
                <p className="text-2xl font-medium" style={{ fontFamily: 'var(--font-heading)' }} data-testid="sub-count-canceled">{canceled}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base" style={{ fontFamily: 'var(--font-heading)' }}>Tutti gli abbonamenti ({subs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {subs.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>Nessun abbonamento ancora sottoscritto.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Cliente</th>
                    <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Email</th>
                    <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Data</th>
                    <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Ricorrenza</th>
                    <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Stato</th>
                    <th className="py-2 pr-3 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(s => (
                    <tr key={s.app_subscription_id} style={{ borderBottom: '1px solid var(--border-light)' }} data-testid={`sub-row-${s.app_subscription_id}`}>
                      <td className="py-3 pr-3 font-medium" style={{ color: 'var(--text-primary)' }}>{(s.billing || {}).name || '-'}</td>
                      <td className="py-3 pr-3" style={{ color: 'var(--text-secondary)' }}>{(s.billing || {}).email || '-'}</td>
                      <td className="py-3 pr-3 text-xs" style={{ color: 'var(--text-muted)' }}>{formatDate(s.created_at)}</td>
                      <td className="py-3 pr-3">{s.amount_recurring?.toFixed(2)} EUR / 3 mesi</td>
                      <td className="py-3 pr-3"><StatusBadge status={s.status} /></td>
                      <td className="py-3 pr-3">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(s)} data-testid={`sub-detail-${s.app_subscription_id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="sub-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Dettaglio Abbonamento</DialogTitle>
            <DialogDescription className="text-xs">{selected?.app_subscription_id}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="flex justify-between items-center">
                <StatusBadge status={selected.status} />
                {selected.status !== 'canceled' && (
                  <Button size="sm" variant="destructive" onClick={handleCancel} disabled={cancelBusy} data-testid="sub-cancel-btn">
                    <X className="w-3.5 h-3.5 mr-1" /> {cancelBusy ? 'Annullo...' : 'Annulla abbonamento'}
                  </Button>
                )}
              </div>

              <Separator />

              <div>
                <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>CLIENTE</p>
                <p><strong>{(selected.billing || {}).name}</strong></p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{(selected.billing || {}).email}</p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>CF: {(selected.billing || {}).tax_code}</p>
                <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Rivista digitale &middot; nessuna spedizione</p>
              </div>

              {selected.invoice && (
                <div>
                  <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>FATTURAZIONE</p>
                  {selected.invoice.company_name && <p className="text-xs">{selected.invoice.company_name}</p>}
                  {selected.invoice.vat_number && <p className="text-xs">P.IVA: {selected.invoice.vat_number}</p>}
                  {selected.invoice.sdi_code && <p className="text-xs">SDI: {selected.invoice.sdi_code}</p>}
                </div>
              )}

              <Separator />

              <div>
                <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>PAGAMENTI</p>
                <p className="text-xs">Prima uscita: <strong>{selected.amount_first?.toFixed(2)} EUR</strong></p>
                <p className="text-xs">Ricorrente: <strong>{selected.amount_recurring?.toFixed(2)} EUR</strong> ogni {selected.interval_count} mesi</p>
                <p className="text-xs">Fatture pagate: <strong>{selected.invoice_count || 0}</strong></p>
                {selected.last_invoice_at && <p className="text-xs">Ultima fattura: {formatDate(selected.last_invoice_at)}</p>}
              </div>

              {invoices.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>STORICO FATTURE</p>
                    <div className="space-y-1.5">
                      {invoices.map(inv => (
                        <div key={inv.stripe_invoice_id} className="flex items-center justify-between text-xs p-2 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)' }}>
                          <div>
                            <p><strong>{inv.amount_paid?.toFixed(2)} EUR</strong> &middot; {inv.billing_reason}</p>
                            <p style={{ color: 'var(--text-muted)' }}>{formatDate(inv.created_at)}</p>
                          </div>
                          {inv.hosted_invoice_url && (
                            <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="flex items-center gap-1" style={{ color: 'var(--accent)' }}>
                              Vedi <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {selected.stripe_subscription_id && (
                <>
                  <Separator />
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    Stripe Sub ID: <code style={{ fontFamily: 'var(--font-mono)' }}>{selected.stripe_subscription_id}</code>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
